import { ArrowForwardRounded, ExpandLess, ExpandMore } from '@mui/icons-material';
import { TableRow, TableCell, Collapse, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { theme } from '../../../theme';
import StaffReviewItemDetails from './StaffReviewItemDetails';
import ActionDropdown from './ActionDropdown';
import OverdueChip from './OverdueChip';
import ReviewerFeedback from './ReviewerFeedback';
import { parseISO, format } from 'date-fns';
import CopyButton from '../../CustomComponents/CopyButton';

function StaffReviewItemEntry(props) {
    const isExpanded = props.selectedItem === props.item.id;

    const reviewData = useMemo(() => {
        if (props.variant === 'implementer') {
            if (props.implementer_review) {
                return props.implementer_review;
            } else if (props.clinical_review) {
                return props.clinical_review;
            }
        } else if (props.variant === 'clinical') {
            return props.clinical_review;
        }

        return null;
    }, [props.variant, props.implementer_review, props.clinical_review]);

    const feedbackOpen = useMemo(() => {
        return reviewData?.status === 'rejected' || isExpanded;
    }, [reviewData, isExpanded]);

    function handleItemClick() {
        if (isExpanded) {
            props.setSelectedItem(null);
        } else {
            props.setSelectedItem(props.item.id);
        }
    }

    function isCustomEdit(rec) {
        return rec?.type === 'custom_user_edit' || rec?.status.split('_')[0] === 'custom';
    }

    function isRemoval(rec) {
        return rec?.status === 'custom_removal' || (rec?.type === 'removal' && !isCustomEdit(rec));
    }

    function isSwap(rec) {
        return rec?.type === 'swap' || rec?.type === 'compliance' || rec?.status === 'custom_swap';
    }

    const recTypeText = {
        addition: 'Add to Card',
        removal: 'Remove from Card',
        update: 'Quantity Change',
        custom_removal: 'Remove from Card',
        custom_update: 'Quantity Change',
        custom_addition: 'Add to Card',
        custom_swap: <Stack direction='row' alignItems='center'>Swap to New Item <ArrowForwardRounded size='small' style={{ paddingLeft: '8px', color: theme.palette.subtext.main }}/></Stack>,
        swap: <Stack direction='row' alignItems='center'>Swap to New Item <ArrowForwardRounded size='small' style={{ paddingLeft: '8px', color: theme.palette.subtext.main }}/></Stack>,
        compliance: <Stack direction='row' alignItems='center'>Swap to New Item <ArrowForwardRounded size='small' style={{ paddingLeft: '8px', color: theme.palette.subtext.main }}/></Stack>,
    };

    function getRecTypeText(rec) {
        if (isCustomEdit(rec)) {
            return recTypeText[rec.status];
        }
        return recTypeText[rec.type];
    }

    return (
        <>
            <TableRow style={{ borderTop: '1px solid', borderColor: theme.palette.border.main }} >
                <TableCell style={{ cursor: 'pointer' }} onClick={handleItemClick}>
                    { isExpanded ? 
                        <ExpandLess style={{ color: theme.palette.subtext.main, cursor: 'pointer' }} /> :
                        <ExpandMore style={{ color: theme.palette.subtext.main, cursor: 'pointer' }} />
                    }
                </TableCell>
                <TableCell style={{ cursor: 'pointer' }} onClick={handleItemClick}>
                    <Stack>
                        <Typography variant='normal' fontSize='14px'>{props.item.name}</Typography>
                        <Typography variant='normal' fontSize='12px'>{props.item.vendor.name} | {props.item.part_number}</Typography>
                    </Stack>
                </TableCell>
                <TableCell>
                    <Stack>
                        <Stack direction='row' spacing={0.5} alignItems='center'>
                            <Typography variant='normal' fontSize='14px'>{props.item.external_id}</Typography>
                            <CopyButton textToCopy={props.item.external_id} />
                        </Stack>
                    </Stack>
                </TableCell>
                <TableCell>
                    {props.recommendation ? getRecTypeText(props.recommendation) : 'None'}
                </TableCell>
                { !isSwap(props.recommendation) &&
                    <>
                        <TableCell style={{ textAlign: 'center' }}>
                            {props.item?.open_quantity ?? '-'}
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                            {props.item?.hold_quantity ?? '-'}
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                            {isRemoval(props.recommendation) ? '-' : (props.recommendation?.custom_open ?? props.recommendation?.open_quantity)}
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                            {isRemoval(props.recommendation) ? '-' : (props.recommendation?.custom_hold ?? props.recommendation?.hold_quantity)}
                        </TableCell>
                    </>
                }
                { isSwap(props.recommendation) &&
                    <>
                        <TableCell style={{ textAlign: 'left' }} colSpan={2}>
                            <Typography variant='normal' fontSize='14px'><strong>New Item: </strong>{props.recommendation?.recommended_item.name}</Typography>
                        </TableCell>
                        <TableCell style={{ textAlign: 'left' }} colSpan={2}>
                            <Typography variant='normal' fontSize='14px'><strong>New ID: </strong>{props.recommendation?.recommended_item.external_id}</Typography>
                            <CopyButton textToCopy={props.recommendation?.recommended_item.external_id} />
                        </TableCell>
                    </>
                }
                <TableCell style={{ padding: 0, textAlign: 'end' }}>
                    <Stack direction='row' alignItems='center' justifyContent='flex-end'>
                        { reviewData?.type === 'clinical' && reviewData?.is_overdue && reviewData?.status === 'open' &&
                            <OverdueChip />
                        }
                        { props.recommendation && reviewData &&
                            <ActionDropdown 
                                variant={props.variant}
                                implementer_review={props.recommendation.implementer_review}
                                clinical_review={props.recommendation.clinical_review}
                                review={reviewData}
                                recommendation={props.recommendation} 
                                disabled={props.disabled} 
                                handleUpdate={props.handleUpdate} 
                            />
                        }
                    </Stack>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ padding: 0 }} colSpan={9}>
                    <Collapse in={feedbackOpen} timeout="auto" unmountOnExit>
                        <Stack direction='row' p={2} spacing={2} alignItems='center' justifyContent='flex-end' backgroundColor={theme.palette.background.darkGray}>
                            { reviewData && reviewData.status === 'rejected' && 
                                <ReviewerFeedback 
                                    review={reviewData} 
                                    recommendation={props.recommendation} 
                                    handleUpdate={props.handleUpdate} 
                                    disabled={props.disabled}
                                />
                            }
                            <Stack>
                                { props.recommendation?.clinical_review && props.recommendation?.clinical_review.reviewed_at !== null && 
                                    <Typography fontSize='12px' minWidth='fit-content'><strong>Card Manager: </strong> {props.recommendation.clinical_review.editor?.first_name} {props.recommendation.clinical_review.editor?.last_name}, {format(parseISO(props.clinical_review.reviewed_at), 'M/d/yy')} </Typography>
                                }
                                { props.recommendation?.implementer_review && props.recommendation.implementer_review?.reviewed_at !== null && 
                                    <Typography fontSize='12px' minWidth='fit-content'><strong>Implementer: </strong> {props.recommendation.implementer_review.editor?.first_name} {props.recommendation.implementer_review.editor?.last_name}, {format(parseISO(props.implementer_review.reviewed_at), 'M/d/yy')} </Typography>
                                }
                            </Stack>
                        </Stack>
                    </Collapse>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <StaffReviewItemDetails item={props.item} recommendation={props.recommendation} review={reviewData} />
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default StaffReviewItemEntry;

